import { LoadingState } from '@lcs/shared/api';
import { Comparer, createEntityAdapter, EntityAdapter, IdSelector } from '@ngrx/entity';
import {
   Action,
   ActionCreator,
   ActionReducer,
   createSelector,
   createReducer as ngrxCreateReducer,
   on,
   ReducerTypes,
} from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { onReset } from 'src/app/authentication/reset-reducer';
import {
   LoadFailureAction,
   LoadFailureActionCreator,
   LoadPageFailureAction,
   LoadPageFailureActionCreator,
   LoadPageSuccessAction,
   LoadPageSuccessActionCreator,
   LoadSuccessAction,
   LoadSuccessActionCreator,
   RegisterActions,
} from './register-actions';
import { RegisterOptions } from './register-options';
import { RegisterSelectors } from './register-selectors';
import { RegisterState, SystemOrAccountRegisterState } from './register-state';

export interface RegisterAdapter<
   TItem,
   TRegisterOptions extends RegisterOptions,
   TOrderingOptions extends OrderingOption
> {
   getInitialState(): RegisterState<TItem, TRegisterOptions, TOrderingOptions>;
   // eslint-disable-next-line @typescript-eslint/ban-types
   getInitialState<TState extends object>(
      additionalState: Omit<TState, keyof RegisterState<TItem, TRegisterOptions, TOrderingOptions>>
   ): RegisterState<TItem, TRegisterOptions, TOrderingOptions> & TState;

   createReducer<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(
      actions: RegisterActions<TItem>,
      initialState: TState,
      ...ons: ReducerTypes<TState, ActionCreator[]>[]
   ): ActionReducer<TState, Action>;

   createOns<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(
      actions: RegisterActions<TItem>
   ): ReducerTypes<TState, ActionCreator[]>[];

   getSelectors(): RegisterSelectors<
      TItem,
      RegisterState<TItem, TRegisterOptions, TOrderingOptions>,
      TRegisterOptions,
      TOrderingOptions
   >;
   getSelectors<TState>(
      selectState: (state: TState) => RegisterState<TItem, TRegisterOptions, TOrderingOptions>
   ): RegisterSelectors<TItem, TState, TRegisterOptions, TOrderingOptions>;

   load<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(state: TState): TState;

   loadSuccess<
      TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>,
      TLoadSuccessAction extends LoadSuccessAction<TItem>
   >(
      state: TState,
      action: TLoadSuccessAction
   ): TState;
   loadFailure<
      TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>,
      TFailureAction extends LoadFailureAction
   >(
      state: TState,
      action: TFailureAction
   ): TState;
   loadPage<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(state: TState): TState;
   loadPageSuccess<
      TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>,
      TLoadPageSuccessAction extends LoadPageSuccessAction<TItem>
   >(
      state: TState,
      action: TLoadPageSuccessAction
   ): TState;
   loadPageFailure<
      TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>,
      TLoadPageFailureAction extends LoadPageFailureAction
   >(
      state: TState,
      action: TLoadPageFailureAction
   ): TState;
}

export interface RegisterAdapterOptions<TItem> {
   selectId?: IdSelector<TItem>;
   sortComparer?: false | Comparer<TItem>;
}

export function createRegisterAdapter<
   TItem,
   TRegisterOptions extends RegisterOptions,
   TOrderingOptions extends OrderingOption
>(options: RegisterAdapterOptions<TItem>): RegisterAdapter<TItem, TRegisterOptions, TOrderingOptions> {
   const entityAdapter: EntityAdapter<TItem> = createEntityAdapter<TItem>(options);

   type S = RegisterState<TItem, TRegisterOptions, TOrderingOptions>;

   function load<TState extends S>(state: TState): TState {
      return {
         ...entityAdapter.removeAll(state),
         callState: LoadingState.Loading,
         endCursor: null,
         hasNextPage: false,
         pagingCallState: LoadingState.Init,
         totalCount: 0,
      };
   }
   function loadSuccess<TState extends S, TLoadSuccessAction extends LoadSuccessAction<TItem>>(
      state: TState,
      action: TLoadSuccessAction
   ): TState {
      return {
         ...entityAdapter.setAll(action.items, state),
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }
   function loadFailure<TState extends S, TLoadFailureAction extends LoadFailureAction>(
      state: TState,
      action: TLoadFailureAction
   ): TState {
      return {
         ...state,
         callState: { error: action },
      };
   }

   function loadPage<TState extends S>(state: TState): TState {
      return {
         ...state,
         pagingCallState: LoadingState.Loading,
      };
   }

   function loadPageSuccess<TState extends S, TLoadPageSuccessAction extends LoadPageSuccessAction<TItem>>(
      state: TState,
      action: TLoadPageSuccessAction
   ): TState {
      return {
         ...entityAdapter.addMany(action.items, state),
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         pagingCallState: LoadingState.Loaded,
         totalCount: action.totalCount,
      };
   }
   function loadPageFailure<TState extends S, TLoadFailureAction extends LoadFailureAction>(
      state: TState,
      action: TLoadFailureAction
   ): TState {
      return {
         ...state,
         pagingCallState: { error: action.error },
      };
   }

   const getInitialState = createInitialStateFactory<TItem, TRegisterOptions, TOrderingOptions>(entityAdapter);

   function createOns<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(
      actions: RegisterActions<TItem>,
      ...ons: ReducerTypes<TState, ActionCreator[]>[]
   ): ReducerTypes<TState, ActionCreator[]>[] {
      return [
         on<TState, ActionCreator[]>(actions.pageEnter, load),
         on<TState, [LoadSuccessActionCreator<TItem>]>(actions.loadSuccess, loadSuccess),
         on<TState, [LoadFailureActionCreator]>(actions.loadFailure, loadFailure),
         on<TState, ActionCreator[]>(actions.loadPage, loadPage),
         on<TState, [LoadPageSuccessActionCreator<TItem>]>(actions.loadPageSuccess, loadPageSuccess),
         on<TState, [LoadPageFailureActionCreator]>(actions.loadPageFailure, loadPageFailure),
         ...ons,
      ];
   }

   function createReducer<TState extends RegisterState<TItem, TRegisterOptions, TOrderingOptions>>(
      actions: RegisterActions<TItem>,
      initialState: TState,
      ...ons: ReducerTypes<TState, ActionCreator[]>[]
   ): ActionReducer<TState, Action> {
      return ngrxCreateReducer(initialState, onReset(initialState), ...createOns<TState>(actions, ...ons));
   }

   function getSelectors(
      selectState?: (state: any) => RegisterState<TItem, TRegisterOptions, TOrderingOptions>
   ): RegisterSelectors<TItem, any, TRegisterOptions, TOrderingOptions> {
      const selectHasNextPage = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.hasNextPage;
      };
      const selectCallState = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.callState;
      };
      const selectLoading = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.callState === LoadingState.Loading;
      };
      const selectOrderingOptions = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.orderingOptions;
      };
      const selectPagingCallState = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.pagingCallState;
      };
      const selectEndCursor = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.endCursor;
      };
      const selectTotalCount = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.totalCount;
      };
      const selectIds = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.ids;
      };
      const selectQuickFilters = (state: RegisterState<TItem, TRegisterOptions, TOrderingOptions>) => {
         return state.quickFilters;
      };

      if (!selectState) {
         return {
            ...entityAdapter.getSelectors(),
            selectCallState,
            selectEndCursor,
            selectHasNextPage,
            selectLoading,
            selectOrderingOptions,
            selectPagingCallState,
            selectQuickFilters,
            selectTotalCount,
            selectIds,
         };
      }

      return {
         ...entityAdapter.getSelectors(selectState),
         selectCallState: createSelector(selectState, selectCallState),
         selectEndCursor: createSelector(selectState, selectEndCursor),
         selectHasNextPage: createSelector(selectState, selectHasNextPage),
         selectLoading: createSelector(selectState, selectLoading),
         selectOrderingOptions: createSelector(selectState, selectOrderingOptions),
         selectPagingCallState: createSelector(selectState, selectPagingCallState),
         selectQuickFilters: createSelector(selectState, selectQuickFilters),
         selectTotalCount: createSelector(selectState, selectTotalCount),
      };
   }

   return {
      ...entityAdapter,
      createOns,
      createReducer,
      getInitialState,
      getSelectors,
      load,
      loadFailure,
      loadPage,
      loadPageFailure,
      loadPageSuccess,
      loadSuccess,
   };
}

function getInitialRegisterState<
   TItem,
   TRegisterOptions extends RegisterOptions,
   TOrderingOptions extends OrderingOption
>(
   entityAdapter: EntityAdapter<TItem>,
   additionalState?: any
): RegisterState<TItem, TRegisterOptions, TOrderingOptions> {
   return {
      ...entityAdapter.getInitialState(additionalState),
      callState: LoadingState.Init,
      columns: null,
      defaultColumns: null,
      endCursor: null,
      hasNextPage: false,
      orderingOptions: null,
      pagingCallState: LoadingState.Init,
      quickFilters: null,
      searchTerm: null,
      totalCount: 0,
   };
}

function createInitialStateFactory<
   TItem,
   TRegisterOptions extends RegisterOptions,
   TOrderingOptions extends OrderingOption
>(entityAdapter: EntityAdapter<TItem>) {
   function getInitialState(): RegisterState<TItem, TRegisterOptions, TOrderingOptions>;
   function getInitialState<TState extends Record<string, unknown>>(
      additionalState?: TState
   ): RegisterState<TItem, TRegisterOptions, TOrderingOptions> {
      return Object.assign(getInitialRegisterState(entityAdapter, additionalState));
   }

   return getInitialState;
}
